import {
	Airing,
	AiringInput,
	AlternateIdNamespaces,
	Channel,
	ChannelInput,
	EditorialHighlight,
	EditorialHighlightInput,
	ExclusionList,
	ExclusionListInput,
	ExclusionPeriod,
	Fer,
	FerInput,
	Game,
	GameInput,
	GameTime,
	GameTimeInput,
	Highlight,
	HighlightInput,
	Offset,
	OffsetInput,
	Period,
	PeriodInput,
	Source,
	SourceInput,
	Team,
	TeamInput,
	VideoAsset,
	VideoAssetInput
} from './types';
import { v4 as uuidv4 } from 'uuid';
import { isNull, omitBy, pick } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export const convertAiringToAiringInput = (airing: Airing): AiringInput => {
	const airingInput = pick(airing, [
		'channelId',
		'status',
		'id',
		'startTime',
		'businessLogic',
		'offsets',
		'clientExclusions',
		'exclusionPlayIds',
		'programSynopsis',
		'programStartTime',
		'programEndTime',
		'drm',
		'drmProvider'
	]) as AiringInput;

	// Clean up the Merlin Alternate Id value on the Airing
	if (airing.mAltId && airing.mAltId.length > 0) airing.mAltId = airing.mAltId.trim();

	// Add an Airing id if not present
	if (!airingInput.id) airingInput.id = uuidv4();

	if (airingInput.programSynopsis?.trim() === '') airingInput.programSynopsis = null;
	if (airingInput.programStartTime?.trim() === '') airingInput.programStartTime = null;
	if (airingInput.programEndTime?.trim() === '') airingInput.programEndTime = null;

	if (airingInput.drm === true && airing.mAltId)
		airingInput.alternateIds = [
			{
				id: `airing#${AlternateIdNamespaces.MERLIN_ALTERNATE_ID}:${airing.mAltId}`,
				alternateId: `airing#${airingInput.id}`,
				namespace: AlternateIdNamespaces.MERLIN_ALTERNATE_ID
			}
		];

	return {
		...airingInput,
		...(airing.offsets?.items && {
			offsets: {
				items: airing.offsets.items.map((offset) => convertOffsetToOffsetInput(offset))
			}
		})
	};
};

export const convertChannelToChannelInput = (channel: Channel): ChannelInput => {
	const channelInput = pick(channel, ['id', 'name', 'language', 'territory', 'type', 'highlights', 'mediaLiveId', 'mediaPackageId']);
	const channelInputWithoutNulls = omitBy(channelInput, isNull);
	return {
		...channelInputWithoutNulls,
		...(channel.sourceAssets?.items && {
			sourceAssets: {
				items: channel.sourceAssets.items.map((videoAsset) => convertVideoAssetToVideoAssetInput(videoAsset))
			}
		})
	};
};

export const convertVideoAssetToVideoAssetInput = (videoAsset: VideoAsset): VideoAssetInput => {
	const videoAssetInput = pick(videoAsset, ['id', 'format', 'url', 'cdnIdentifierArn']);
	const videoAssetInputWithoutNulls = omitBy(videoAssetInput, isNull);
	return {
		...videoAssetInputWithoutNulls
	} as unknown as VideoAssetInput;
};

export const convertGameTimeToGameTimeInput = (gameTime: GameTime): GameTimeInput => {
	const gameTimeInput = pick(gameTime, ['segment', 'segmentId', 'position']);
	if (isNaN(gameTimeInput.segment)) {
		gameTimeInput.segment = -1;
		gameTimeInput.segmentId = '-1';
	}
	return omitBy(gameTimeInput, isNull) as unknown as GameTimeInput;
};

export const convertGameToGameInput = (game: Game): GameInput => {
	const gameInput = pick(game, ['id', 'sport', 'startTime', 'title', 'offsets', 'excludeScheduling']) as GameInput;
	return {
		...gameInput,
		...(game.offsets?.items && {
			offsets: {
				items: game.offsets.items.map((offset) => convertOffsetToOffsetInput(offset))
			}
		})
	};
};

export const convertOffsetToOffsetInput = (offset: Offset): OffsetInput => {
	const offsetInput = pick(offset, ['segment', 'offset']);
	const offsetInputWithoutNulls = omitBy(offsetInput, isNull);
	return {
		...offsetInputWithoutNulls
	} as unknown as OffsetInput;
};

export const convertHighlightToHighlightInput = (highlight: Highlight): HighlightInput => {
	const highlightInput = pick(highlight, ['id', 'gameId', 'airingId', 'title', 'type', 'channelId']);
	const highlightInputWithoutNulls = omitBy(highlightInput, isNull);
	return {
		...highlightInputWithoutNulls,
		...(highlight.periods?.items && {
			periods: {
				items: highlight.periods.items.map((period) => convertPeriodToPeriodInput(period))
			}
		})
	} as unknown as HighlightInput;
};

export const convertEditorialHighlightToEditorialHighlightInput = (editorialHighlight: EditorialHighlight): EditorialHighlightInput => {
	const editorialHighlightInput = pick(editorialHighlight, ['id', 'gameId', 'title', 'type']);
	const editorialHighlightInputWithoutNulls = omitBy(editorialHighlightInput, isNull);
	return {
		...editorialHighlightInputWithoutNulls,
		...(editorialHighlight.periods?.items && {
			periods: {
				items: editorialHighlight.periods.items.map((period) => convertPeriodToPeriodInput(period))
			}
		})
	} as unknown as EditorialHighlightInput;
};

export const convertPeriodToPeriodInput = (period: Period): PeriodInput => {
	const periodInput = pick(period, ['description', 'shortText', 'gameEndTime', 'startGameTime', 'teamId', 'title', 'type', 'endScore', 'playId']);
	const periodInputWithoutNulls = omitBy(periodInput, isNull);
	return {
		...periodInputWithoutNulls,
		source: convertSourceToSourceInput(period.source),
		...(period.startGameTime && {
			startGameTime: convertGameTimeToGameTimeInput(period.startGameTime)
		}),
		...(period.team && period.team.id && { team: convertTeamToTeamInput(period.team) })
	} as unknown as PeriodInput;
};

export const convertSourceToSourceInput = (source: Source): SourceInput => {
	const sourceInput = pick(source, ['type', 'channelId', 'startTime', 'endTime', 'gameId']);
	sourceInput.startTime = dayjs(sourceInput.startTime ?? '')
		.utc()
		.format('YYYY-MM-DDTHH:mm:ssZ')
		.toString();
	sourceInput.endTime = dayjs(sourceInput.endTime ?? '')
		.utc()
		.format('YYYY-MM-DDTHH:mm:ssZ')
		.toString();
	return omitBy(sourceInput, isNull) as unknown as SourceInput;
};

export const convertTeamToTeamInput = (team: Team): TeamInput => pick(team, ['id']);

export const convertExclusionListToExclusionListInput = (exclusionList: ExclusionList): ExclusionListInput => {
	const exclusionListInput = pick(exclusionList, ['id', 'airingId', 'gameId', 'periods']);

	exclusionListInput.periods = {
		items:
			exclusionList.periods?.items?.map(
				(period: ExclusionPeriod): ExclusionPeriod => pick(period, ['id', 'playId', 'title', 'shortText', 'startTime', 'endTime', 'endpoint'])
			) || null
	};

	return exclusionListInput as unknown as ExclusionListInput;
};

export const convertFerToFerInput = (gameId: string, fer: Fer): FerInput => {
	const ferInput = pick(fer, ['channelId', 'startTime']) as FerInput;
	ferInput.gameId = gameId;

	return {
		...ferInput
	};
};
